import React, { useState, type ReactElement } from 'react';
import { styled } from '@compiled/react';
import { SpotlightCard, SpotlightManager, SpotlightTransition } from '@atlaskit/onboarding';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { layers } from '@atlassian/jira-common-styles/src/main.tsx';
import CoordinationClient from '@atlassian/jira-engagement/src/ui/coordination-client/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { useIntl } from '@atlassian/jira-intl';
import {
	fireUIAnalytics,
	useAnalyticsEvents,
	FireUiAnalytics,
} from '@atlassian/jira-product-analytics-bridge';
import { cardKeys as spotlightCardKeys } from '../../../../controllers/spotlight-coordination/constants.tsx';
import { useSpotlightCoordinationActions } from '../../../../controllers/spotlight-coordination/index.tsx';
import { getEngagementPlatformlMessageId, TOUR_MESSAGE_ID, views } from '../../../constants.tsx';
import messages from './messages.tsx';

export type SpotlightCardInnerProps = {
	mode: string;
	view: string;
	hasIssueResults?: boolean;
	isAiEnabled: boolean;
};

export type SpotlightCardProps = {
	mode: string;
	view: string;
	hasIssueResults?: boolean;
	isAiEnabled: boolean;
};

type StopFunction = () => void;
type CoordinationStopProviderProps = {
	stop?: StopFunction;
	children: (arg1: StopFunction | undefined) => ReactElement;
};

export const CoordinationStopProvider = ({ stop, children }: CoordinationStopProviderProps) =>
	children(stop);

export const SpotlightTourCardInner = ({
	mode,
	view,
	hasIssueResults = false,
	isAiEnabled,
}: SpotlightCardInnerProps) => {
	const [isSpotlightActive, setIsSpotlightActive] = useState(true);

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();
	const { onSetActiveCard } = useSpotlightCoordinationActions();

	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'ninSpotlightTour',
	});

	const fireStartButtonAnalyticsEvent = (stop?: StopFunction) => {
		if (isAiEnabled) {
			// @ts-expect-error Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'
			fireUIAnalytics(analyticsEvent.clone(), 'start', {
				isAiEnabled,
			});
			onSetActiveCard(spotlightCardKeys.GLOBAL_SCOPE_JQL_BUILDER_NL_TO_JQL, stop);
			return;
		}

		// @ts-expect-error Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'
		fireUIAnalytics(analyticsEvent.clone(), 'start', {
			mode,
			view: hasIssueResults ? view : views.detail,
			hasIssueResults,
		});
		stop && stop();
	};

	const fireDismissButtonAnalyticsEvent = () => {
		// @ts-expect-error Argument of type 'UIAnalyticsEvent | null' is not assignable to parameter of type 'UIAnalyticsEvent'
		fireUIAnalytics(analyticsEvent.clone(), 'dismiss');
	};
	if (isSpotlightActive) {
		return (
			<CoordinationClient
				messageId={getEngagementPlatformlMessageId(TOUR_MESSAGE_ID)}
				messageType="engagement"
			>
				<CoordinationStopProvider>
					{(stop) => (
						<SpotlightTransition>
							<SpotlightWrapper data-testid="issue-navigator-changeboarding.common.ui.spotlight.spotlight-card">
								<SpotlightCard
									key="nin.changeboarding.start"
									width={364}
									heading={formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.spotlightTitleIssueTermRefresh
											: messages.spotlightTitle,
									)}
									actions={[
										{
											text: (
												<Text
													weight="medium"
													testId="issue-navigator-changeboarding.common.ui.spotlight.spotlight-card.start"
												>
													{formatMessage(messages.spotlightButtonStartTourText)}
												</Text>
											),
											onClick: () => {
												setIsSpotlightActive(false);
												fireStartButtonAnalyticsEvent(stop);
											},
										},
										{
											text: (
												<Text
													weight="medium"
													testId="issue-navigator-changeboarding.common.ui.spotlight.spotlight-card.dismiss"
												>
													{formatMessage(messages.spotlightButtonDismissText)}
												</Text>
											),
											onClick: () => {
												setIsSpotlightActive(false);
												fireDismissButtonAnalyticsEvent();
												stop?.();
											},
											appearance: 'subtle',
										},
									]}
								>
									{formatMessage(
										expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
											? messages.spotlightDescIssueTermRefresh
											: messages.spotlightDesc,
									)}
									<FireUiAnalytics
										action="viewed"
										actionSubject="screen"
										actionSubjectId="ninSpotlightStartCard"
									/>
								</SpotlightCard>
							</SpotlightWrapper>
						</SpotlightTransition>
					)}
				</CoordinationStopProvider>
			</CoordinationClient>
		);
	}

	return <></>;
};

const SpotlightTourCard = ({ mode, view, hasIssueResults, isAiEnabled }: SpotlightCardProps) => (
	<SpotlightManager>
		<SpotlightTourCardInner
			mode={mode}
			view={view}
			hasIssueResults={hasIssueResults}
			isAiEnabled={isAiEnabled}
		/>
	</SpotlightManager>
);
export default SpotlightTourCard;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SpotlightWrapper = styled.div({
	position: 'fixed',
	marginTop: token('space.200'),
	right: token('space.200'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: layers.spotlight,
});
